import { CACHE_TIME, PAGINATION, PROJECT_STATUS } from '@hygge/shared';
import { useQuery } from '@tanstack/react-query';
import { projectsKeys, useProjects } from '@/hooks/use-projects';
import { PROJECT_FILTER_QUERY, type ProjectsQueryParams } from '@/lib/projects';
import { getReportProjects } from '@/lib/time-entries';

/**
 * What `/projects` is asked for when somebody is choosing where to record time.
 *
 * `status` was absent before, and `/projects` filters by it only when asked
 * (`projects.service.ts` — `if (query.status) where.status = query.status`), so
 * the picker offered archived and on-hold projects beside the active ones.
 * Nothing here names a project, which is what makes it a standing rule rather
 * than a cleanup: the next project archived leaves the list on its own.
 *
 * `active` and not "anything but archived": confirmed by Kateryna on
 * 2026-09-02, on-hold projects are not offered either. Work on them is paused,
 * so new hours should not be landing there.
 *
 * Asked of the server rather than filtered over the fetched array, because the
 * array has already been cut to one page by the time it arrives — filtering
 * here would shrink that page instead of reaching the projects it never held.
 *
 * `MAX_LIMIT` is the largest page the endpoint accepts; `PaginationQueryDto`
 * rejects more. Its ceiling is a backend-local constant, so this pins the same
 * number and not the same definition. The default page of 20 is what made the
 * picker drop every project past the twentieth by name; past 100 that silence
 * returns one ceiling higher, and the list would need paging or a search.
 *
 * Order is left to the endpoint, which sorts by name ascending when asked for
 * nothing — the order the picker wants anyway, and the one the Projects page
 * relies on too, so it cannot change quietly here alone.
 */
export const TIME_TRACKING_PROJECT_QUERY = {
  status: PROJECT_STATUS.ACTIVE,
  limit: PAGINATION.MAX_LIMIT,
} as const satisfies ProjectsQueryParams;

/**
 * The projects a time-tracking picker may offer.
 *
 * Both pickers read this one hook rather than spelling the query out twice —
 * the same reason `timer-visibility.ts` keeps one flag for two surfaces. It
 * covers the surfaces that ASK which project; a timer started from a task
 * takes the board's project with nothing to choose from
 * (`time-tracking-section.tsx`), and no list can filter that.
 *
 * Nor is this list an authority on whether a project may be written to. It is
 * narrowed to the caller's own projects for anyone holding
 * `projects.view.assigned`, and cut to one page besides, so a project missing
 * from it is as likely to be one this reader cannot see. Refusing a write is
 * the server's to do, and it does: a project that is not active takes no new
 * time through the time tracker's entry and timer routes — refused with a 400
 * naming its state, or, on a timer stop, not written and named in `unsaved`
 * (`project-open.ts` in the time-tracker module). The Clockify import is not
 * covered [HW-935].
 *
 * The project filters OVER RECORDED HOURS deliberately do not use this and still
 * offer archived projects — behind a "Show archived" toggle since HW-951 —
 * because an archived project still holds the hours logged against it and a
 * report about it is a fair question. They share
 * `PROJECT_FILTER_QUERY` in `lib/projects.ts`, read from four places —
 * `use-filter-options.ts` twice, for the Report Builder's `projects` and
 * `projectManagers` sources, and the filters on `dashboard-tab.tsx` and
 * `reports-tab.tsx` [HW-903]. It asks for the same page size as this query and,
 * unlike it, for no status at all — difference enough to give the two separate
 * cache entries, which is what they want: these two lists are not the same list.
 * All four send it to the Time Tracker through `useReportProjects` below, not
 * to `/projects`. [HW-972]
 *
 * The Clockify import's mapping offers archived projects for the same reason,
 * filing historic hours against projects that may since have been archived. It
 * reaches neither query: that screen takes its options from the mappings
 * endpoint and never calls `useProjects`. [HW-874]
 */
export function useTimeTrackingProjects() {
  return useProjects(TIME_TRACKING_PROJECT_QUERY);
}

/**
 * Where the report filters' projects come from: the Time Tracker's
 * `GET /time-entries/report-projects`, not `/projects`.
 *
 * `/projects` narrows a role holding only `projects.view.assigned` to its
 * memberships, while a reader holding `time.reports.all` or `time.edit.all`
 * reads every project's hours in these reports — rows from projects the
 * filter could not name. The Time Tracker answers each reader with the
 * projects their reports can contain, and anyone else with the projects
 * `/projects` would list them — of each only what a filter shows
 * (`ReportProject`). Every status, as `PROJECT_FILTER_QUERY` asks; archived and
 * on-hold projects stay behind the filters' "Show archived" toggle (HW-951).
 *
 * Keyed under `projectsKeys.lists()`, which a project create, edit or archive
 * invalidates, so the filters refresh as they did on `/projects`, under a key
 * of its own so the two answers never share an entry. [HW-972]
 */
export const reportProjectsQuery = {
  queryKey: [...projectsKeys.lists(), 'report-projects', PROJECT_FILTER_QUERY] as const,
  queryFn: () => getReportProjects(PROJECT_FILTER_QUERY),
};

/**
 * The report filters' projects — the Reports tab's and the Dashboard's
 * Project filters and the Report Builder's `projects` and `projectManagers`
 * sources. One query, so the screens share one request. The route admits a
 * holder of either `time.reports.generate` (Reports, Builder) or
 * `time.reports.team` (Dashboard). [HW-972]
 */
export function useReportProjects() {
  return useQuery({ ...reportProjectsQuery, staleTime: CACHE_TIME.DEFAULT });
}
